import { useEffect, useRef, useState } from "react";
import {
  type ChildAgeBand,
  type ChildAgeBandsState,
  useChildAgeBands,
} from "@platform/plan-grid-core";

import { EditMode, parseCustomText } from "./formatters";

import type { JsonValue } from "../utils/popupCustomText";
import type { ICellEditorParams } from "ag-grid-community";
import type { Dispatch, SetStateAction } from "react";

export interface ChildAgeBandsCellEditorParams {
  initialBands: ChildAgeBand[];
  initialCustomText?: string | null;
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

export type ChildAgeBandsEditorProps = ICellEditorParams &
  ChildAgeBandsCellEditorParams;

export interface ChildAgeBandsEditor extends ChildAgeBandsState {
  draftCustomText: string;
  setDraftCustomText: Dispatch<SetStateAction<string>>;
  editMode: EditMode;
  setEditMode: Dispatch<SetStateAction<EditMode>>;
  customTextSupported: boolean;
  editingCustomText: boolean;
  getValue: () => unknown;
  handleSave: () => void;
  handleCancel: () => void;
}

/**
 * State and save/cancel logic for ChildBenefitAgeBandsCellEditor. Band state,
 * mutation, and validation come from useChildAgeBands (shared with Build's
 * ChildAgeBandsEditor); this hook adds Present's own custom-text mode on top.
 * The editor edits the `childBenefitAmountsByAge` row directly, so `getValue`
 * returns the whole band array (or `[]` when the analyst leaves no bands -
 * unset) and AG Grid writes it straight back to the row; no collapsed-sibling
 * writes needed.
 */
export function useChildAgeBandsEditor(
  props: ChildAgeBandsEditorProps,
): ChildAgeBandsEditor {
  const {
    initialBands,
    initialCustomText,
    onCustomTextChange,
    api,
    colDef,
    column,
    node,
    stopEditing,
  } = props;

  const editor = useChildAgeBands(initialBands);

  const [draftCustomText, setDraftCustomText] = useState<string>(
    initialCustomText ?? "",
  );
  const [editMode, setEditMode] = useState<EditMode>(
    initialCustomText ? "customText" : "benefit",
  );

  const lastValueRef = useRef<unknown>(initialBands);

  useEffect(() => {
    setDraftCustomText(initialCustomText ?? "");
    setEditMode(initialCustomText ? "customText" : "benefit");
  }, [initialCustomText]);

  const customTextSupported =
    initialCustomText !== undefined || onCustomTextChange !== undefined;
  const editingCustomText = customTextSupported && editMode === "customText";

  const saveCustomText = (): void => {
    onCustomTextChange?.(
      parseCustomText(draftCustomText),
      initialBands as unknown as JsonValue,
    );
    stopEditing(true);
  };

  const handleSave = (): void => {
    if (editingCustomText) {
      return saveCustomText();
    }

    const value = editor.getSaveValue();
    if (value === null) {
      return;
    }

    lastValueRef.current = value;
    if (initialCustomText) onCustomTextChange?.(null);

    stopEditing(true);

    // stopEditing(true) cancels AG Grid's own commit, so persist explicitly:
    // setDataValue updates the cell and fires onCellValueChanged, which stamps
    // the dirty cell and triggers the save PATCH. This mirrors the other Quotes
    // popup editors (GuaranteeIssue, FieldPopoverEditor); relying on getValue
    // alone would neither display the value nor save it.
    setTimeout(() => {
      const colKey = column ?? colDef?.field;
      if (colKey && node) {
        node.setDataValue(colKey, value);
        api.refreshCells({ rowNodes: [node], force: true });
      }
    }, 0);
  };

  const handleCancel = (): void => {
    lastValueRef.current = initialBands;
    stopEditing(true);
  };

  return {
    ...editor,
    draftCustomText,
    setDraftCustomText,
    editMode,
    setEditMode,
    customTextSupported,
    editingCustomText,
    getValue: (): unknown => lastValueRef.current,
    handleSave,
    handleCancel,
  };
}
